import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Avatar,
  Stack,
} from "@mui/material";
import styled from "styled-components";
import { useSelector } from "react-redux";
import UserFace from "../../../images/user.png";
import BaseButton from "../../UI/BaseButton/BaseButton";
import { PictureNew } from "../../../util/API/customers";
import CustomAlert from "../../UI/CustomAlert/CustomAlert";
import { getUserDetails, editUserDetails } from "../../../util/API/customers";
import { RenderRoleBadges } from "../UserProfile";
import { Skeleton } from "@mui/material";

const StyledTextField = styled(TextField)({
  "& .MuiInputBase-root": {
    maxWidth: "300px",
  },
});

function ProfileDetails({ pictureData, setPictureData }) {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    dateofbirth: "",
    telephoneno: "",
    cityname: "",
    stateid: "",
    countryid: "",
    defaultcurrency: "",
  });

  const [initialFormData, setInitialFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasNewImage, setHasNewImage] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [alert, setAlert] = useState({
    showAlert: false,
    message: "",
    severity: "",
    header: "",
  });

  let user = useSelector((state) => state.login);
  let requiredConstants = useSelector((state) => state.constants);
  let isOperator = sessionStorage.getItem("isOperator");
  let configVariables = useSelector((state) => state.config.baseConfig);
  let imageUrl = configVariables.imageURL;

  const hasChanges =
    hasNewImage ||
    (initialFormData !== null &&
      Object.keys(formData).some(
        (key) => formData[key] !== initialFormData[key]
      ));

  useEffect(() => {
    async function getUserData() {
      setLoadingData(true);
      const userDetails = await getUserDetails(user["user"].id);

      setPictureData({
        photo: imageUrl + userDetails?.User?.photo,
        base64Image: imageUrl + userDetails?.User?.photo,
      });

      const fetched = {
        firstName: userDetails?.User?.firstName || "",
        lastName: userDetails?.User?.lastName || "",
        dateofbirth: userDetails?.User?.dateofbirth || "",
        telephoneno: userDetails?.User?.telephoneno || "",
        cityname: userDetails?.User?.cityname || "",
        stateid: userDetails?.User?.stateid || "",
        countryid: userDetails?.User?.countryid || "",
        defaultcurrency: userDetails?.User?.defaultcurrency || "",
      };

      setFormData(fetched);
      setInitialFormData(fetched);
      setLoadingData(false);
    }

    if (user["user"]?.id) {
      getUserData();
    }
  }, [user["user"]?.id]);

  const handleFieldChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const onImageChange = (event) => {
    if (event.target.files && event.target.files[0]) {
      let img = event.target.files[0];
      let reader = new FileReader();
      reader.onload = (e) => {
        setPictureData({
          photo: e.target.result,
          base64Image: e.target.result,
        });
        setHasNewImage(true);
      };
      reader.readAsDataURL(img);
    }
  };

  const onSubmit = async (e) => {
    setLoading(true);
    try {
      if (hasNewImage && pictureData?.base64Image) {
        const pictureNewResponse = await PictureNew(
          user["user"].id,
          pictureData?.base64Image,
        );
        if (!pictureNewResponse) {
          setAlert({
            showAlert: true,
            message: "Failed to upload picture",
            severity: "error",
            header: "Error",
          });
          setLoading(false);
          return;
        }
        setHasNewImage(false);
      }

      const list = [
        `firstName|${formData.firstName}`,
        `lastName|${formData.lastName}`,
        `dob|${formData.dateofbirth}`,
        `telephoneno|${formData.telephoneno}`,
        `cityname|${formData.cityname}`,
        `stateid|${formData.stateid}`,
        `countryid|${formData.countryid}`,
        `defaultcurrency|${formData.defaultcurrency}`,
      ].join("//");

      const email =
        isOperator === "true"
          ? user["user"].other1
          : user["email"].emailAccount;

      const response = await editUserDetails(
        user["user"].id,
        list,
        email,
        false,
        formData.firstName,
        "",
      );

      if (response?.Status === "1") {
        const refreshed = await getUserDetails(user["user"].id);
        const updated = {
          firstName: refreshed?.User?.firstName || "",
          lastName: refreshed?.User?.lastName || "",
          dateofbirth: refreshed?.User?.dateofbirth || "",
          telephoneno: refreshed?.User?.telephoneno || "",
          cityname: refreshed?.User?.cityname || "",
          stateid: refreshed?.User?.stateid || "",
          countryid: refreshed?.User?.countryid || "",
          defaultcurrency: refreshed?.User?.defaultcurrency || "",
        };
        setFormData(updated);
        setInitialFormData(updated);
        setPictureData({ photo: "", base64Image: "" });
        setHasNewImage(false);
        setAlert({
          showAlert: true,
          message: "Profile updated successfully.",
          severity: "success",
          header: "Success",
        });
      } else {
        setAlert({
          showAlert: true,
          message: "Failed to update profile.",
          severity: "error",
          header: "Error",
        });
      }
    } catch (err) {
      console.error("onSubmit error:", err);
      setAlert({
        showAlert: true,
        message: "Something went wrong.",
        severity: "error",
        header: "Error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography
        variant="h6"
        sx={{ marginTop: 2, fontFamily: "Nunito" }}
        gutterBottom
      >
        Profile Details
      </Typography>

      <Box
        sx={{
          display: "flex",
          p: 3,
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: 0.2,
          flexDirection: "row",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Avatar
            sx={{ width: 100, height: 100 }}
            src={
              pictureData.photo == imageUrl || pictureData.photo == ""
                ? UserFace
                : pictureData.photo
            }
            alt="Profile Picture"
          />
          <Button
            variant="outlined"
            sx={{ display: "block", marginTop: 2 }}
            component="label"
          >
            <input
              type="file"
              accept="image/*"
              onChange={onImageChange}
              style={{ display: "none" }}
            />
            Upload/Edit
          </Button>
          <Stack direction="row" spacing={1} sx={{ marginTop: 2 }}>
            {user?.securityRole ? (
              <RenderRoleBadges
                userRoles={user?.securityRole}
                systemRoles={requiredConstants.systemRoles}
              />
            ) : (
              "No roles assigned"
            )}
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            marginLeft: 2,
            width: "100%",
          }}
        >
          {loadingData ? (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
                marginBottom: 15,
              }}
            >
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton
                  key={i}
                  variant="rectangular"
                  height={40}
                  sx={{ borderRadius: 1 }}
                />
              ))}
            </Box>
          ) : (
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: 2,
                marginBottom: 15,
              }}
            >
              <StyledTextField
                variant="standard"
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Birth Date"
                name="dateofbirth"
                value={formData.dateofbirth}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Currency"
                name="defaultcurrency"
                value={formData.defaultcurrency}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Phone"
                name="telephoneno"
                value={formData.telephoneno}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="City"
                name="cityname"
                value={formData.cityname}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="State"
                name="stateid"
                value={formData.stateid}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Country"
                name="countryid"
                value={formData.countryid}
                onChange={handleFieldChange}
              />
              <StyledTextField
                variant="standard"
                label="Email"
                value={
                  isOperator === "true"
                    ? user["user"].other1
                    : user["email"].emailAccount
                }
                InputProps={{ readOnly: true }}
                disabled
              />
              <StyledTextField
                variant="standard"
                label="Default Login"
                value={user["email"].emailAccount}
                InputProps={{ readOnly: true }}
                disabled
              />

              <Box
                sx={{
                  gridColumn: "span 2",
                  display: "flex",
                  justifyContent: "flex-end",
                  mt: 3,
                }}
              >
                <BaseButton
                  buttonText={loading ? "Saving..." : "Save Changes"}
                  onClick={onSubmit}
                  disabled={loading || !hasChanges}
                  buttonStyle="save-button"
                />
              </Box>
            </Box>
          )}
        </Box>
      </Box>


      <CustomAlert
        severity={alert.severity}
        show={alert.showAlert}
        header={alert.header}
        message={alert.message}
        onClick={(prevDetails) =>
          setAlert({ ...prevDetails, showAlert: false })
        }
        autoclose={alert.severity === "error" ? false : true}
      />
    </Box>
  );
}

export default ProfileDetails;